import { Host, DefaultAppHandler } from '@livefree/applications';
import { Authorizing, ThemeProvider } from '@livefree/react-ui';
import { Outlet, useHref, useNavigate } from 'react-router';
// Import RouterProvider from @heroui/react (not react-aria-components) so it
// uses the SAME react-aria-components instance as HeroUI's components. The host
// resolves a different RAC version than HeroUI bundles, and a RouterProvider
// from a mismatched instance sets a context HeroUI never reads.
import { RouterProvider } from '@heroui/react';
import { ShellConfig, hostRuntime } from '@/core';
import { APIProvider } from '@vis.gl/react-google-maps';

// Connects React Aria's client-side navigation to React Router so that `href`
// props on HeroUI/RAC components (ListBox.Item, Link, Menu.Item, …) route
// client-side. HeroUI v3 removed its provider; without this, `href` falls back
// to a full-page browser navigation instead of routing.
const AriaRouterProvider = ({ children }) => {
  const navigate = useNavigate();
  return (
    <RouterProvider navigate={navigate} useHref={useHref}>
      {children}
    </RouterProvider>
  );
};

export const BaseLayout = Host.bootstrap(
  () => {
    return (
      <APIProvider apiKey={ShellConfig.current.googleMapsKey}>
        <AriaRouterProvider>
          <ThemeProvider>
            <DefaultAppHandler />
            <Outlet />
          </ThemeProvider>
        </AriaRouterProvider>
      </APIProvider>
    );
  },
  {
    auth: {
      applicationKey: ShellConfig.current.auth.applicationKey,
      authorityUrl: ShellConfig.current.auth.host,
      clientId: ShellConfig.current.auth.clientId,
      authorizationParams: {
        offlineAccess: true,
        scope: ShellConfig.current.auth.scopes,
        usePkce: true,
        useState: true,
      },
      sessionParams: {
        pollSessionStatus: false,
        autoRefreshToken: true,
        validateExistingTokenOnLoad: false,
      },
      discovery: {
        authorizeUrl: '/connect/authorize',
        tokenUrl: '/connect/token',
        userInfoUrl: '/connect/userinfo',
        signOutUrl: '/connect/endsession',
        sessionFrameUrl: 'connect/checksession',
      },
    },
    rootPath: ShellConfig.current.preferences.rootPath,
    runtime: hostRuntime,
  },
  () => (
    <ThemeProvider>
      <Authorizing />
    </ThemeProvider>
  ),
);
